import { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';

import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  IconCalendarOutline,
  Spacer,
} from '@zillow/constellation';
import { Label, TextButton } from '@zillow/constellation-10';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { getFormattedDateForDatePicker } from '../../utils/dateUtils';

import type { ChangeEvent, KeyboardEvent } from 'react';

type Props = {
  initialDate?: string;
  label?: string;
  inputName?: string;
  onDateChange: (newValue: string) => void;
  onInputBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  showNextMonthButton?: boolean;
  setDefaultDate?: boolean;
  minDate?: string;
  setMaxDateOneYearAhead?: boolean;
  defaultPastDateToToday?: boolean;
  saveOnDateSelect?: boolean;
};

const DatePickerWithDateSelect = ({
  initialDate = '',
  label = 'Date available',
  onDateChange,
  onInputBlur = () => {},
  inputName,
  showNextMonthButton = true,
  setDefaultDate = true,
  minDate,
  setMaxDateOneYearAhead = false,
  defaultPastDateToToday = true,
  saveOnDateSelect = true,
}: Props) => {
  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const [pickedDate, setPickedDate] = useState('');
  const [firstOfNextMonth, setFirstOfNextMonth] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const isInputFocused = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const lastClickedElement = useRef<EventTarget | null>(null);

  const today = new Date();
  const startOfYesterday = new Date(today.getTime() - 24 * 3600 * 1000);

  const firstDate = minDate ? new Date(minDate) : startOfYesterday;
  const lastDate = new Date(firstDate.getTime() + 24 * 3600 * 1000).setFullYear(firstDate.getFullYear() + 1);

  useEffect(() => {
    const startOfNextMonth = format(new Date(today.getFullYear(), today.getMonth() + 1), 'MM/dd/yyyy');
    const formattedDate = getFormattedDateForDatePicker({ initialDate, today, defaultPastDateToToday });

    if (initialDate || setDefaultDate) {
      setPickedDate(formattedDate);
      onDateChange(formattedDate);
    }
    setFirstOfNextMonth(startOfNextMonth);
  }, [initialDate]);

  // Track the last clicked element globally
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      lastClickedElement.current = event.target;
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, []);

  const handleChange = (event: { date: Date }) => {
    const formatted = format(event.date, 'MM/dd/yyyy');
    setPickedDate(formatted);
    onDateChange(formatted);
    setShowDatePicker(false);
    // Explicitly trigger save with the latest value
    if (saveOnDateSelect && formatted) {
      // @ts-expect-error: We only need target.name and target.value for onInputBlur, not a full event object
      onInputBlur({ target: { name: inputName, value: formatted } });
    }
  };

  const handleBlur = (event: ChangeEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const lastClicked = lastClickedElement.current as Node | null;
      let node = lastClicked;
      let insideDatePicker = false;
      while (node) {
        if (node instanceof HTMLElement) {
          if (node.className.includes('DatePicker') || node.className.includes('calendar')) {
            insideDatePicker = true;
            break;
          }
        }
        node = node.parentNode;
      }
      if (insideDatePicker) {
        return;
      }
      setShowDatePicker(false);
      if (pickedDate) {
        // @ts-expect-error: We only need target.name and target.value for onInputBlur, not a full event object
        onInputBlur({ target: { name: inputName, value: pickedDate } });
      }
    }, 0);
  };

  const handleSetMonthKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && event.target instanceof HTMLButtonElement) {
      event.target.click();
    }
  };

  const handleSetMonthOnClick = () => {
    handleChange({ date: new Date(firstOfNextMonth) });
    // @ts-expect-error: We only need target.name and target.value for onInputBlur, not a full event object
    onInputBlur({ target: { name: inputName, value: firstOfNextMonth } });
  };

  const renderInput = () => {
    return (
      <AdornedInput
        onFocus={() => {
          setShowDatePicker(true);
        }}
        input={
          <DatePickerInput
            ref={inputRef}
            size={isScreenDesktop ? 'lg' : 'md'}
            datepicker={
              showDatePicker ? (
                <div ref={calendarRef}>
                  <DatePicker
                    minDate={firstDate}
                    maxDate={setMaxDateOneYearAhead ? lastDate : undefined}
                    selected={pickedDate ? new Date(pickedDate) : undefined}
                    onChange={handleChange}
                    onFocus={() => {
                      isInputFocused.current = true;
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        isInputFocused.current = false;
                      }, 10);
                    }}
                  />
                </div>
              ) : (
                <></>
              )
            }
            placeholder="MM/DD/YYYY"
            value={pickedDate}
            onChange={(val: string) => {
              setPickedDate(val);
              onDateChange(val);
            }}
            onBlur={handleBlur}
            name={inputName}
          />
        }
        rightAdornment={
          <Adornment>
            <IconCalendarOutline size="sm" />
          </Adornment>
        }
      />
    );
  };

  return (
    <div data-testid="DatePickerContainer">
      <Label>{label}</Label>
      <Spacer marginBottom="xxs" />

      {renderInput()}

      {showNextMonthButton && (
        <TextButton
          tone="brand"
          onClick={handleSetMonthOnClick}
          onKeyUp={handleSetMonthKeyUp}
          type="button"
          css={{ marginTop: '600' }}
        >
          {`Set to ${firstOfNextMonth}`}
        </TextButton>
      )}
    </div>
  );
};

export default DatePickerWithDateSelect;
